/**
 * Strategy Lab Coach API client — `/strategy-rd/v1/coach/*`.
 * FE never calls TES/MCP directly.
 */

import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow, throwFromErrorBody } from "../utils/apiRequestError";
import { STRATEGY_RD_API_BASE, STRATEGY_RD_DISABLED_SLUG } from "../types/strategyRd";
import type {
  StrategyRdCoachLabAction,
  StrategyRdCoachResultCard,
  StrategyRdCoachChip,
  StrategyRdCoachComposerBody,
  StrategyRdCoachComposerPatch,
  StrategyRdCoachFocus,
  StrategyRdCoachIntakeRequest,
  StrategyRdCoachIntakeResponse,
  StrategyRdCoachJournalHintsRequest,
  StrategyRdCoachJournalHintsResponse,
  StrategyRdCoachLabSeed,
  StrategyRdCoachLabSnapshot,
  StrategyRdCoachOrientationFaq,
  StrategyRdCoachOrientationResponse,
  StrategyRdCoachProfileAnswers,
  StrategyRdCoachProfileOption,
  StrategyRdCoachProfileQuestion,
  StrategyRdCoachProfileResponse,
  StrategyRdCoachStyle,
  StrategyRdCoachStylesResponse,
  StrategyRdCoachThreadDetail,
  StrategyRdCoachThreadMessage,
  StrategyRdCoachThreadSummary,
  StrategyRdCoachThreadsListResponse,
  StrategyRdCoachTurnRequest,
  StrategyRdCoachTurnResponse,
  StrategyRdCoachUsage,
  StrategyRdCoachActivity,
  StrategyRdCoachTurnTrace,
  StrategyRdCoachValidatePatchRequest,
  StrategyRdCoachValidatePatchResponse,
  StrategyRdCoachWowSeedResponse,
} from "../types/strategyRdCoach";
import {
  isStrategyRdDisabledError,
  parseStrategyRdHeadline,
  parseStrategyRdHoldoutSplit,
  parseStrategyRdCostStressVerdict,
  parseStrategyRdStability,
  parseStrategyRdSweep,
  StrategyRdDisabledError,
} from "./strategyRdApi";

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string {
  return typeof value === "string" ? value : value == null ? "" : String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value == null) return null;
  const s = asString(value).trim();
  return s || null;
}

function asNumber(value: unknown, fallback = 0): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function asBoolean(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

async function coachFetch(
  path: string,
  init?: RequestInit & { signal?: AbortSignal },
  fallbackMessage = "TES request failed."
): Promise<unknown> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}${path}`, init);
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const rec = asRecord(body);
    const slug =
      asOptionalString(rec?.error) ||
      asOptionalString(rec?.slug) ||
      asOptionalString(asRecord(rec?.detail)?.slug) ||
      asOptionalString(asRecord(rec?.detail)?.error);
    if (slug === STRATEGY_RD_DISABLED_SLUG) {
      throw new StrategyRdDisabledError();
    }
    throwFromErrorBody(response.status, body, fallbackMessage);
  }
  return parseJsonOrThrow(response, fallbackMessage);
}

export function parseStrategyRdCoachUsage(raw: unknown): StrategyRdCoachUsage {
  const r = asRecord(raw) || {};
  return {
    plan: asString(r.plan) || "standard",
    turns_used: asNumber(r.turns_used),
    turns_cap: asNumber(r.turns_cap),
    turns_remaining: asNumber(r.turns_remaining),
    tokens_used: asNumber(r.tokens_used),
    tokens_cap: asNumber(r.tokens_cap),
    tokens_remaining: asNumber(r.tokens_remaining),
    soft_warn: asBoolean(r.soft_warn),
    window: asString(r.window) || "rolling_7d_utc",
    week_start: asOptionalString(r.week_start),
    backtests_used: r.backtests_used != null ? asNumber(r.backtests_used) : null,
    backtests_cap: r.backtests_cap != null ? asNumber(r.backtests_cap) : null,
    backtests_remaining: r.backtests_remaining != null ? asNumber(r.backtests_remaining) : null,
    mode: r.mode === "credits" || r.mode === "prompts" ? r.mode : null,
    backtests_window: r.backtests_window === "week" || r.backtests_window === "month" ? r.backtests_window : null,
    disclaimer: asOptionalString(r.disclaimer),
    product_name: asOptionalString(r.product_name),
    product_expand: asOptionalString(r.product_expand),
  };
}

function parseChip(raw: unknown): StrategyRdCoachChip | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  const label = asOptionalString(r.label);
  if (!id || !label) return null;
  return {
    id,
    label,
    pack_id: asOptionalString(r.pack_id),
    style_id: asOptionalString(r.style_id),
    goal: asOptionalString(r.goal),
    faq_id: asOptionalString(r.faq_id),
  };
}

function parseChips(raw: unknown): StrategyRdCoachChip[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseChip).filter((c): c is StrategyRdCoachChip => Boolean(c));
}

function parseStyle(raw: unknown): StrategyRdCoachStyle | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  const name = asOptionalString(r.name);
  if (!id || !name) return null;
  return {
    id,
    name,
    pack_id: asOptionalString(r.pack_id),
    description: asOptionalString(r.description) || asOptionalString(r.blurb),
    blurb: asOptionalString(r.blurb),
    family: asOptionalString(r.family),
    default_candle_type: asOptionalString(r.default_candle_type),
    default_session_ids: Array.isArray(r.default_session_ids)
      ? r.default_session_ids.map((s) => asString(s)).filter(Boolean)
      : null,
    alt_pack_ids: Array.isArray(r.alt_pack_ids)
      ? r.alt_pack_ids.map((s) => asString(s)).filter(Boolean)
      : null,
    families: Array.isArray(r.families)
      ? r.families.map((s) => asString(s)).filter(Boolean)
      : null,
    goals: Array.isArray(r.goals)
      ? r.goals.map((s) => asString(s)).filter(Boolean)
      : null,
  };
}

export function parseStrategyRdCoachStyles(raw: unknown): StrategyRdCoachStylesResponse {
  const r = asRecord(raw) || {};
  const styles = Array.isArray(r.styles)
    ? r.styles.map(parseStyle).filter((s): s is StrategyRdCoachStyle => Boolean(s))
    : [];
  return {
    styles,
    packs: Array.isArray(r.packs)
      ? r.packs.filter((p): p is Record<string, unknown> => Boolean(asRecord(p)))
      : [],
    catalog_only: r.catalog_only !== false,
  };
}

function parseComposer(raw: unknown): StrategyRdCoachComposerBody | null {
  const r = asRecord(raw);
  return r ? (r as StrategyRdCoachComposerBody) : null;
}

function parsePatch(raw: unknown): StrategyRdCoachComposerPatch | null {
  const r = asRecord(raw);
  if (!r) return null;
  const ops = Array.isArray(r.ops) ? r.ops.filter((o) => asRecord(o)) : [];
  if (ops.length === 0) return null;
  return { ops: ops as StrategyRdCoachComposerPatch["ops"] };
}

export function parseStrategyRdCoachIntake(raw: unknown): StrategyRdCoachIntakeResponse {
  const r = asRecord(raw) || {};
  return {
    style_id: asOptionalString(r.style_id),
    pack_id: asOptionalString(r.pack_id),
    goal: asOptionalString(r.goal),
    composer: parseComposer(r.composer),
    scan_seed: asRecord(r.scan_seed),
    normalized: asRecord(r.normalized),
    suggested_chips: parseChips(r.suggested_chips),
    goals: parseChips(r.goals),
    catalog_only: r.catalog_only !== false,
  };
}

export function parseStrategyRdCoachResultCard(raw: unknown): StrategyRdCoachResultCard | null {
  const r = asRecord(raw);
  const hid = asOptionalString(r?.hypothesis_id);
  if (!r || !hid) return null;
  const bySide = asRecord(r.by_side);
  return {
    hypothesis_id: hid,
    name: asOptionalString(r.name),
    version: r.version != null ? asNumber(r.version) : null,
    stats: parseStrategyRdHeadline(r.stats),
    by_side: bySide
      ? Object.fromEntries(Object.entries(bySide).map(([k, v]) => [k, parseStrategyRdHeadline(v)]))
      : null,
    split: parseStrategyRdHoldoutSplit(r.split),
    stability: parseStrategyRdStability(r.stability),
    equity_curve: Array.isArray(r.equity_curve)
      ? r.equity_curve.flatMap((p) => {
          const row = asRecord(p);
          const t = asOptionalString(row?.t);
          const equity = row ? Number(row.equity) : Number.NaN;
          return t && Number.isFinite(equity) ? [{ t, equity }] : [];
        })
      : [],
    warnings: Array.isArray(r.warnings) ? r.warnings.map((w) => asString(w)).filter(Boolean) : null,
    robustness: asRecord(r.robustness)
      ? {
          verdict: asOptionalString(asRecord(r.robustness)?.verdict),
          stale: asBoolean(asRecord(r.robustness)?.stale),
          cost_stress: parseStrategyRdCostStressVerdict(asRecord(r.robustness)?.cost_stress),
        }
      : null,
    sweep: parseStrategyRdSweep(r.sweep),
    market: asRecord(r.market)
      ? {
          symbol: asOptionalString(asRecord(r.market)?.symbol),
          candle_type: asOptionalString(asRecord(r.market)?.candle_type),
        }
      : null,
    period: asRecord(r.period)
      ? { start: asOptionalString(asRecord(r.period)?.start), end: asOptionalString(asRecord(r.period)?.end) }
      : null,
    net_pnl: r.net_pnl != null && Number.isFinite(Number(r.net_pnl)) ? Number(r.net_pnl) : null,
  };
}

export function parseStrategyRdCoachLabActions(raw: unknown): StrategyRdCoachLabAction[] | null {
  if (!Array.isArray(raw) || raw.length === 0) return null;
  const out = raw.flatMap((item) => {
    const r = asRecord(item);
    const jobId = asOptionalString(r?.job_id);
    if (!r || !jobId) return [];
    const progress = asRecord(r.progress);
    return [
      {
        kind: asString(r.kind) || "backtest",
        job_id: jobId,
        status: asString(r.status) || "queued",
        poll_url: asOptionalString(r.poll_url),
        hypothesis_id: asOptionalString(r.hypothesis_id),
        progress: progress
          ? {
              stage: asString(progress.stage),
              done: progress.done != null ? asNumber(progress.done) : null,
              total: progress.total != null ? asNumber(progress.total) : null,
            }
          : null,
        outcome: asOptionalString(r.outcome),
        card: parseStrategyRdCoachResultCard(r.card),
      },
    ];
  });
  return out.length > 0 ? out : null;
}

export function parseStrategyRdCoachTurnResponse(raw: unknown): StrategyRdCoachTurnResponse {
  const r = asRecord(raw) || {};
  const usageRaw = asRecord(r.usage);
  return {
    reply_markdown: asString(r.reply_markdown),
    suggested_chips: parseChips(r.suggested_chips),
    composer_patch: parsePatch(r.composer_patch),
    template: asRecord(r.template) || asRecord(asRecord(r.meta)?.template) || null,
    template_patch: asRecord(r.template_patch) || asRecord(asRecord(r.meta)?.template_patch) || null,
    sizing: asRecord(r.sizing) || asRecord(asRecord(r.meta)?.sizing) || null,
    sizing_patch: asRecord(r.sizing_patch),
    patch_readback: Array.isArray(r.patch_readback)
      ? r.patch_readback.map((l) => asString(l)).filter(Boolean)
      : null,
    lab_actions: parseStrategyRdCoachLabActions(r.lab_actions),
    usage: usageRaw
      ? {
          turns_remaining:
            usageRaw.turns_remaining != null
              ? asNumber(usageRaw.turns_remaining)
              : undefined,
          tokens_remaining:
            usageRaw.tokens_remaining != null
              ? asNumber(usageRaw.tokens_remaining)
              : undefined,
          soft_warn:
            usageRaw.soft_warn != null ? asBoolean(usageRaw.soft_warn) : undefined,
          turns_used:
            usageRaw.turns_used != null ? asNumber(usageRaw.turns_used) : undefined,
          turns_cap:
            usageRaw.turns_cap != null ? asNumber(usageRaw.turns_cap) : undefined,
          tokens_used:
            usageRaw.tokens_used != null ? asNumber(usageRaw.tokens_used) : undefined,
          tokens_cap:
            usageRaw.tokens_cap != null ? asNumber(usageRaw.tokens_cap) : undefined,
          plan: asOptionalString(usageRaw.plan) || undefined,
          window: asOptionalString(usageRaw.window) || undefined,
          backtests_used:
            usageRaw.backtests_used != null ? asNumber(usageRaw.backtests_used) : undefined,
          backtests_cap:
            usageRaw.backtests_cap != null ? asNumber(usageRaw.backtests_cap) : undefined,
          backtests_remaining:
            usageRaw.backtests_remaining != null
              ? asNumber(usageRaw.backtests_remaining)
              : undefined,
        }
      : null,
    source: asOptionalString(r.source),
    catalog_only: r.catalog_only !== false,
    persisted: asBoolean(r.persisted),
    thread_id: asOptionalString(r.thread_id),
    disclaimer: asOptionalString(r.disclaimer),
    product_name: asOptionalString(r.product_name),
    product_expand: asOptionalString(r.product_expand),
  };
}

export function parseStrategyRdCoachValidatePatch(
  raw: unknown
): StrategyRdCoachValidatePatchResponse {
  const r = asRecord(raw) || {};
  return {
    ...r,
    ok: r.ok !== false && !r.error,
    composer: parseComposer(r.composer),
    error: asOptionalString(r.error),
  };
}

export function parseStrategyRdCoachWowSeed(raw: unknown): StrategyRdCoachWowSeedResponse {
  const r = asRecord(raw) || {};
  const window = asRecord(r.window);
  return {
    wow: asBoolean(r.wow),
    symbol: asOptionalString(r.symbol),
    window: window
      ? {
          start: asOptionalString(window.start) || undefined,
          end: asOptionalString(window.end) || undefined,
          lookback_days:
            window.lookback_days != null ? asNumber(window.lookback_days) : undefined,
        }
      : null,
    style_id: asOptionalString(r.style_id),
    intake: r.intake ? parseStrategyRdCoachIntake(r.intake) : null,
    next_steps: Array.isArray(r.next_steps)
      ? r.next_steps.map((s) => asString(s)).filter(Boolean)
      : null,
    opening_chips: parseChips(r.opening_chips),
    catalog_only: r.catalog_only !== false,
  };
}

function parseThreadSummary(raw: unknown): StrategyRdCoachThreadSummary | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  if (!id) return null;
  return {
    id,
    title: asOptionalString(r.title) || "New Lab chat",
    thread_summary: asOptionalString(r.thread_summary),
    updated_at: asOptionalString(r.updated_at),
    created_at: asOptionalString(r.created_at),
    message_count:
      r.message_count != null ? asNumber(r.message_count) : null,
    hypothesis_ids: Array.isArray(r.hypothesis_ids)
      ? r.hypothesis_ids.map((x) => asString(x)).filter(Boolean)
      : [],
  };
}

function parseThreadMessage(raw: unknown): StrategyRdCoachThreadMessage | null {
  const r = asRecord(raw);
  if (!r) return null;
  const role = asOptionalString(r.role);
  const content = asString(r.content);
  if (!role) return null;
  return {
    id: asOptionalString(r.id) || undefined,
    role,
    content,
    meta: asRecord(r.meta),
    created_at: asOptionalString(r.created_at),
  };
}

function parseLabSnapshot(raw: unknown): StrategyRdCoachLabSnapshot | null {
  const r = asRecord(raw);
  if (!r) return null;
  return {
    ...r,
    composer: parseComposer(r.composer),
    goal: asOptionalString(r.goal),
    style_id: asOptionalString(r.style_id),
    pack_id: asOptionalString(r.pack_id),
    last_job_id: asOptionalString(r.last_job_id),
    scan_summary: asRecord(r.scan_summary) as StrategyRdCoachLabSnapshot["scan_summary"],
    outcome_summary: asRecord(r.outcome_summary),
    focus: asRecord(r.focus) as StrategyRdCoachFocus | null,
  };
}

export function parseStrategyRdCoachThreadsList(
  raw: unknown
): StrategyRdCoachThreadsListResponse {
  const r = asRecord(raw) || {};
  const list = Array.isArray(r.threads) ? r.threads : Array.isArray(raw) ? raw : [];
  return {
    threads: list
      .map(parseThreadSummary)
      .filter((t): t is StrategyRdCoachThreadSummary => Boolean(t)),
  };
}

export function parseStrategyRdCoachThreadDetail(raw: unknown): StrategyRdCoachThreadDetail {
  const summary = parseThreadSummary(raw);
  const r = asRecord(raw) || {};
  const messages = Array.isArray(r.messages)
    ? r.messages
        .map(parseThreadMessage)
        .filter((m): m is StrategyRdCoachThreadMessage => Boolean(m))
    : [];
  return {
    id: summary?.id || asString(r.id),
    title: summary?.title || "New Lab chat",
    thread_summary: summary?.thread_summary ?? asOptionalString(r.thread_summary),
    updated_at: summary?.updated_at,
    created_at: summary?.created_at,
    message_count: summary?.message_count ?? messages.length,
    hypothesis_ids: summary?.hypothesis_ids ?? [],
    messages,
    lab_snapshot: parseLabSnapshot(r.lab_snapshot),
  };
}

/** What TES is doing in the turn in flight (polled while waiting for the reply). */
export async function getStrategyRdCoachActivity(signal?: AbortSignal): Promise<StrategyRdCoachActivity> {
  const raw = (await coachFetch("/coach/activity", { signal })) as Record<string, unknown> | null;
  const steps = Array.isArray(raw?.steps)
    ? (raw!.steps as unknown[])
        .map((x) => x as { label?: unknown; at?: unknown })
        .filter((x) => typeof x.label === "string" && typeof x.at === "number")
        .map((x) => ({ label: x.label as string, at: x.at as number }))
    : [];
  const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
  return { steps, started: num(raw?.started), done: raw?.done === true, finished: num(raw?.finished), now: num(raw?.now) };
}

/** Steps with how long each took, ending at ``end`` (server seconds). */
export function strategyRdCoachTurnTrace(activity: StrategyRdCoachActivity, end: number): StrategyRdCoachTurnTrace {
  const steps = activity.steps.map((s, i) => {
    const next = i + 1 < activity.steps.length ? activity.steps[i + 1].at : end;
    return { label: s.label, seconds: Math.max(0, Math.round(next - s.at)) };
  });
  const start = activity.started ?? activity.steps[0]?.at ?? end;
  return { steps, seconds: Math.max(0, Math.round(end - start)) };
}

export async function getStrategyRdCoachUsage(
  signal?: AbortSignal
): Promise<StrategyRdCoachUsage> {
  const raw = await coachFetch("/coach/usage", { signal });
  return parseStrategyRdCoachUsage(raw);
}

export async function listStrategyRdCoachStyles(
  signal?: AbortSignal
): Promise<StrategyRdCoachStylesResponse> {
  const raw = await coachFetch("/coach/styles", { signal });
  return parseStrategyRdCoachStyles(raw);
}

export async function postStrategyRdCoachIntake(
  body: StrategyRdCoachIntakeRequest,
  signal?: AbortSignal
): Promise<StrategyRdCoachIntakeResponse> {
  const raw = await coachFetch("/coach/intake", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return parseStrategyRdCoachIntake(raw);
}

export async function postStrategyRdCoachTurn(
  body: StrategyRdCoachTurnRequest,
  signal?: AbortSignal
): Promise<StrategyRdCoachTurnResponse> {
  const raw = await coachFetch("/coach/turn", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return parseStrategyRdCoachTurnResponse(raw);
}

export async function validateStrategyRdCoachPatch(
  body: StrategyRdCoachValidatePatchRequest,
  signal?: AbortSignal
): Promise<StrategyRdCoachValidatePatchResponse> {
  const raw = await coachFetch("/coach/validate-patch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return parseStrategyRdCoachValidatePatch(raw);
}

export async function getStrategyRdCoachWowSeed(
  symbol?: string | null,
  signal?: AbortSignal
): Promise<StrategyRdCoachWowSeedResponse> {
  const q = symbol?.trim()
    ? `?symbol=${encodeURIComponent(symbol.trim())}`
    : "";
  const raw = await coachFetch(`/coach/wow-seed${q}`, { signal });
  return parseStrategyRdCoachWowSeed(raw);
}

export async function postStrategyRdCoachJournalHints(
  body: StrategyRdCoachJournalHintsRequest,
  signal?: AbortSignal
): Promise<StrategyRdCoachJournalHintsResponse> {
  const raw = await coachFetch("/coach/journal-hints", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  const r = asRecord(raw) || {};
  return {
    ...r,
    chips: parseChips(r.chips ?? r.suggested_chips),
    suggested_chips: parseChips(r.suggested_chips ?? r.chips),
  };
}

/** `hypothesisId`: only the chats that ran or discussed that backtest, newest first. */
export async function listStrategyRdCoachThreads(
  limit = 30,
  signal?: AbortSignal,
  opts?: { hypothesisId?: string | null }
): Promise<StrategyRdCoachThreadsListResponse> {
  const params = new URLSearchParams({ limit: String(Math.min(100, Math.max(1, limit))) });
  if (opts?.hypothesisId) params.set("hypothesis_id", opts.hypothesisId);
  const raw = await coachFetch(`/coach/threads?${params.toString()}`, {
    signal,
  });
  return parseStrategyRdCoachThreadsList(raw);
}

/** `hypothesisId` starts the chat on a backtest that has no chat yet. */
export async function createStrategyRdCoachThread(
  title?: string | null,
  signal?: AbortSignal,
  opts?: { hypothesisId?: string | null }
): Promise<StrategyRdCoachThreadSummary> {
  const body: Record<string, string> = {};
  if (title?.trim()) body.title = title.trim().slice(0, 80);
  if (opts?.hypothesisId) body.hypothesis_id = opts.hypothesisId;
  const raw = await coachFetch("/coach/threads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  const parsed = parseThreadSummary(raw);
  if (!parsed) throw new Error("TES thread create returned no id");
  return parsed;
}

export async function postStrategyRdCoachThreadMessages(
  threadId: string,
  body: {
    messages: Array<{
      role: "user" | "assistant" | "system";
      content: string;
      meta?: Record<string, unknown> | null;
    }>;
  },
  signal?: AbortSignal
): Promise<StrategyRdCoachThreadDetail> {
  const raw = await coachFetch(
    `/coach/threads/${encodeURIComponent(threadId)}/messages`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal,
    },
    "Could not save this help answer."
  );
  return parseStrategyRdCoachThreadDetail(raw);
}

export async function getStrategyRdCoachThread(
  threadId: string,
  signal?: AbortSignal
): Promise<StrategyRdCoachThreadDetail> {
  const raw = await coachFetch(`/coach/threads/${encodeURIComponent(threadId)}`, {
    signal,
  });
  return parseStrategyRdCoachThreadDetail(raw);
}

export async function patchStrategyRdCoachThread(
  threadId: string,
  body: {
    title?: string | null;
    thread_summary?: string | null;
    lab_snapshot?: StrategyRdCoachLabSnapshot | null;
  },
  signal?: AbortSignal
): Promise<StrategyRdCoachThreadDetail> {
  const raw = await coachFetch(`/coach/threads/${encodeURIComponent(threadId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  return parseStrategyRdCoachThreadDetail(raw);
}

export async function deleteStrategyRdCoachThread(
  threadId: string,
  signal?: AbortSignal
): Promise<void> {
  const response = await authenticatedFetch(
    `${STRATEGY_RD_API_BASE}/coach/threads/${encodeURIComponent(threadId)}`,
    { method: "DELETE", signal }
  );
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    throwFromErrorBody(response.status, body, "Could not delete chat.");
  }
}

function parseProfileOption(raw: unknown): StrategyRdCoachProfileOption | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  const label = asOptionalString(r.label);
  if (!id || !label) return null;
  return {
    id,
    label,
    hint: asOptionalString(r.hint),
    style_id: asOptionalString(r.style_id),
  };
}

function parseProfileQuestion(raw: unknown): StrategyRdCoachProfileQuestion | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  const prompt = asOptionalString(r.prompt);
  if (!id || !prompt) return null;
  const options = Array.isArray(r.options)
    ? r.options
        .map(parseProfileOption)
        .filter((o): o is StrategyRdCoachProfileOption => Boolean(o))
    : [];
  return {
    id,
    prompt,
    multi: r.multi === true,
    required: r.required !== false,
    options,
  };
}

export function parseStrategyRdCoachProfile(raw: unknown): StrategyRdCoachProfileResponse {
  const r = asRecord(raw) || {};
  const answers = asRecord(r.answers) || {};
  const q = asRecord(r.questionnaire) || {};
  const questions = Array.isArray(q.questions)
    ? q.questions
        .map(parseProfileQuestion)
        .filter((x): x is StrategyRdCoachProfileQuestion => Boolean(x))
    : [];
  const orientationChips = Array.isArray(r.orientation_starter_chips)
    ? r.orientation_starter_chips
        .map(parseChip)
        .filter((c): c is StrategyRdCoachChip => Boolean(c))
    : null;
  return {
    user_id: asOptionalString(r.user_id),
    answers: answers as StrategyRdCoachProfileAnswers,
    complete: r.complete === true,
    completed_at: asOptionalString(r.completed_at),
    updated_at: asOptionalString(r.updated_at),
    focus: asRecord(r.focus) as StrategyRdCoachFocus | null,
    lab_seed: parseLabSeed(r.lab_seed),
    orientation_starter_chips: orientationChips,
    product_name: asOptionalString(r.product_name),
    product_expand: asOptionalString(r.product_expand),
    disclaimer: asOptionalString(r.disclaimer),
    catalog_only: r.catalog_only !== false,
    questionnaire: {
      questions,
      catalog_only: q.catalog_only !== false,
      note: asOptionalString(q.note),
    },
  };
}

function parseLabSeed(raw: unknown): StrategyRdCoachLabSeed | null {
  const r = asRecord(raw);
  if (!r) return null;
  const intake = asRecord(r.suggested_intake);
  return {
    style_id: asOptionalString(r.style_id),
    candle_type: asOptionalString(r.candle_type),
    alt_candle_types: Array.isArray(r.alt_candle_types)
      ? r.alt_candle_types.map((x) => asString(x).trim()).filter(Boolean)
      : null,
    lookback_days:
      r.lookback_days != null && Number.isFinite(Number(r.lookback_days))
        ? asNumber(r.lookback_days)
        : null,
    session_ids: Array.isArray(r.session_ids)
      ? r.session_ids.map((x) => asString(x).trim()).filter(Boolean)
      : null,
    example_symbols: Array.isArray(r.example_symbols)
      ? r.example_symbols.map((x) => asString(x).trim()).filter(Boolean)
      : null,
    suggested_intake: intake
      ? {
          style_id: asOptionalString(intake.style_id),
          pack_id: asOptionalString(intake.pack_id),
          symbol: asOptionalString(intake.symbol),
          candle_type: asOptionalString(intake.candle_type),
          session_ids: Array.isArray(intake.session_ids)
            ? intake.session_ids.map((x) => asString(x).trim()).filter(Boolean)
            : null,
          trade_side: asOptionalString(intake.trade_side),
          polarity: asOptionalString(intake.polarity),
          goal: asOptionalString(intake.goal),
        }
      : null,
  };
}

function parseOrientationFaq(raw: unknown): StrategyRdCoachOrientationFaq | null {
  const r = asRecord(raw);
  if (!r) return null;
  const id = asOptionalString(r.id);
  const label = asOptionalString(r.label);
  const answer = asOptionalString(r.answer_markdown);
  if (!id || !label || !answer) return null;
  const ctaRaw = asRecord(r.cta);
  return {
    id,
    label,
    category: asOptionalString(r.category),
    answer_markdown: answer,
    suggested_next: Array.isArray(r.suggested_next)
      ? r.suggested_next.map((x) => asString(x).trim()).filter(Boolean)
      : null,
    cta: ctaRaw
      ? {
          type: asString(ctaRaw.type).trim(),
          label: asOptionalString(ctaRaw.label),
        }
      : null,
  };
}

export function parseStrategyRdCoachOrientation(
  raw: unknown
): StrategyRdCoachOrientationResponse {
  const r = asRecord(raw) || {};
  const faqs = Array.isArray(r.faqs)
    ? r.faqs
        .map(parseOrientationFaq)
        .filter((f): f is StrategyRdCoachOrientationFaq => Boolean(f))
    : [];
  const starterChips = Array.isArray(r.starter_chips)
    ? r.starter_chips
        .map(parseChip)
        .filter((c): c is StrategyRdCoachChip => Boolean(c))
    : faqs.map((f) => ({ id: f.id, label: f.label, faq_id: f.id }));
  return {
    faqs,
    starter_chips: starterChips,
    product_name: asOptionalString(r.product_name),
    product_expand: asOptionalString(r.product_expand),
    disclaimer: asOptionalString(r.disclaimer),
    catalog_only: r.catalog_only !== false,
  };
}

function coachThreadQuery(threadId?: string | null): string {
  const id = (threadId || "").trim();
  if (!id) return "";
  return `?thread_id=${encodeURIComponent(id)}`;
}

export async function getStrategyRdCoachOrientation(
  signal?: AbortSignal,
  threadId?: string | null
): Promise<StrategyRdCoachOrientationResponse> {
  const raw = await coachFetch(
    `/coach/orientation${coachThreadQuery(threadId)}`,
    { signal },
    "Could not load TES orientation."
  );
  return parseStrategyRdCoachOrientation(raw);
}

export async function getStrategyRdCoachOrientationFaq(
  faqId: string,
  signal?: AbortSignal,
  threadId?: string | null
): Promise<StrategyRdCoachOrientationFaq> {
  const id = faqId.trim();
  const raw = await coachFetch(
    `/coach/orientation/${encodeURIComponent(id)}${coachThreadQuery(threadId)}`,
    { signal },
    "Could not load orientation FAQ."
  );
  const faq = parseOrientationFaq(raw) || parseOrientationFaq(asRecord(raw)?.faq);
  if (!faq) {
    throw new Error("Orientation FAQ missing answer.");
  }
  return faq;
}

export { isStrategyRdDisabledError };
